import { createPlanQueryService } from './plan_query_service.mjs';
import { PLAN_QUERY_LIMITS } from './plan_query_data.mjs';

const CANCELLED = Symbol('plan-query-cancelled');
const OWNER = /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/;
const reply = (owner, status, code) => Object.freeze({ owner, status, ...(code ? { code } : {}) });

/** Owner-bound asynchronous preparation; query reads themselves stay synchronous and indexed. */
export function createPlanQueryProviderService({ getContext, onError = () => undefined, requestTimeoutMs = 30000 } = {}) {
  if (!Number.isSafeInteger(requestTimeoutMs) || requestTimeoutMs <= 0 || requestTimeoutMs > 120000) {
    throw new TypeError('Invalid plan query timeout');
  }
  const queries = createPlanQueryService({ getContext }), providers = new Map();
  let context = null, generation = 0, sequence = 0, disposed = false;
  function cancel(record) {
    const pending = record.pending; record.pending = null;
    if (pending) { pending.finish(CANCELLED); pending.controller.abort(); }
  }
  function synchronizeContext() {
    let next;
    try { next = queries.synchronizeContext(); }
    catch (error) {
      for (const record of providers.values()) { cancel(record); record.ready = false; }
      context = null; generation++;
      throw error;
    }
    if (context !== next) {
      context = next; generation++;
      for (const record of providers.values()) { cancel(record); record.ready = false; record.error = null; }
    }
    return context;
  }
  function registerProvider(owner, provider) {
    if (disposed || typeof owner !== 'string' || owner.length > 128 || !OWNER.test(owner)
      || typeof provider !== 'function' || providers.has(owner) || providers.size >= PLAN_QUERY_LIMITS.owners) return false;
    providers.set(owner, { provider, pending: null, ready: false, error: null });
    return true;
  }
  function unregisterProvider(owner, provider) {
    const record = providers.get(owner);
    if (!record || record.provider !== provider) return false;
    cancel(record); providers.delete(owner); queries.removeOwner(owner);
    return true;
  }
  async function request(owner, { force = false } = {}) {
    const captured = synchronizeContext(), record = providers.get(owner);
    if (!record) return reply(owner, 'unsupported');
    if (disposed || !captured) return reply(owner, 'unavailable');
    if (record.ready && !force) return reply(owner, 'ready');
    cancel(record); record.ready = false; record.error = null;
    const token = queries.beginUpdate(owner), controller = new AbortController();
    let finish;
    const cancelled = new Promise(resolve => { finish = resolve; });
    const pending = { controller, finish }; record.pending = pending;
    const input = Object.freeze({ owner, generation, requestId: ++sequence,
      modelRevision: captured.modelRevision, geometryRevision: captured.geometryRevision,
      assetRevision: captured.assetRevision, signal: controller.signal });
    const fresh = () => { synchronizeContext(); return !disposed && context === captured
      && providers.get(owner) === record && record.pending === pending; };
    let timer;
    try {
      const timeout = new Promise((_resolve, reject) => {
        timer = setTimeout(() => { const error = new Error('Plan query provider timed out');
          error.code = 'PLAN_QUERY_TIMEOUT'; reject(error); }, requestTimeoutMs);
      });
      // Call unbound. Even a rejected/late provider is observed after timeout or cancellation.
      const provider = record.provider;
      const output = await Promise.race([Promise.resolve().then(() => {
        if (!fresh()) return CANCELLED;
        return provider(input);
      }), cancelled, timeout]);
      if (output === CANCELLED || !fresh()) return reply(owner, 'stale');
      if (!queries.commit(token, output)) return reply(owner, 'stale');
      record.ready = true;
      return reply(owner, 'committed');
    } catch (error) {
      if (!fresh()) return reply(owner, 'stale');
      record.error = typeof error?.code === 'string' ? error.code : 'PLAN_QUERY_PROVIDER_FAILED';
      record.ready = false;
      // Discard the preparation slot as well as any rejected data.
      queries.removeOwner(owner);
      try { const reported = onError(Object.freeze({ owner, code: record.error }));
        if (reported && typeof reported.then === 'function') Promise.resolve(reported).catch(() => undefined);
      } catch { /* Diagnostics must not affect other owners. */ }
      return reply(owner, 'failed', record.error);
    } finally {
      clearTimeout(timer);
      if (record.pending === pending) record.pending = null;
      controller.abort();
    }
  }
  function status(expectedOwners = [...providers.keys()]) {
    synchronizeContext();
    const entries = expectedOwners.map(owner => {
      const record = providers.get(owner);
      return reply(owner, !record ? 'unsupported' : !context || disposed ? 'unavailable'
        : record.error ? 'failed' : record.pending ? 'pending' : record.ready ? 'ready' : 'stale', record?.error);
    });
    return Object.freeze({ ready: entries.length > 0 && entries.every(entry => entry.status === 'ready'),
      entries: Object.freeze(entries), index: queries.status() });
  }
  const read = method => (point, options = {}) => {
    synchronizeContext();
    // Include registered but unprepared/failed owners: a partial index is not a complete answer.
    return queries[method](point, { ...options, owners: options.owners === undefined ? [...providers.keys()] : options.owners });
  };
  return Object.freeze({ registerProvider, unregisterProvider, request, status, synchronizeContext,
    hitTest: read('hitTest'), boxSelect: read('boxSelect'), nearestSnap: read('nearestSnap'),
    isCurrent(result) { synchronizeContext(); return queries.isCurrent(result); },
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const record of providers.values()) cancel(record);
      providers.clear(); queries.dispose(); context = null;
    }
  });
}
